import type { LandIqDataRunEstimate } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { formatDuration } from '@/lib/landiq/format-duration';
import { estimateStanding, minutesToMs } from '@/lib/landiq/run-estimate';

type Props = {
  readonly estimate: LandIqDataRunEstimate;
  readonly running: boolean;
};

const duration = (minutes: number) => formatDuration(minutesToMs(minutes));

/** Actual against estimate, then the overrun in the warning tone, or the time left while the run is under it. */
export function RunEstimate({ estimate, running }: Props) {
  const t = useTranslations('pages.admin.landiq-data-run.metrics.estimate');
  const standing = estimateStanding(estimate, running);

  return (
    <span className="tabular-nums">
      {t('actual-of', { actual: duration(estimate.actualMinutes), estimate: duration(estimate.estimateMinutes) })}
      {standing.kind === 'OVER' && (
        <>
          {' · '}
          <span className="font-medium text-warning-700">
            {t('over', { overrun: duration(standing.overrunMinutes), percent: standing.percent })}
          </span>
        </>
      )}
      {standing.kind === 'LEFT' && (
        <>
          {' · '}
          <span className="text-text-tertiary">{t('left', { left: duration(standing.leftMinutes) })}</span>
        </>
      )}
    </span>
  );
}

/** The manifest's estimate alone, for an entry: `Schätzung 5 h`. */
export function PlannedEstimate({ minutes, className }: { readonly minutes: number; readonly className?: string }) {
  const t = useTranslations('pages.admin.landiq-data-run.metrics.estimate');

  return <span className={className}>{t('planned', { duration: duration(minutes) })}</span>;
}
